import React, { useState, useEffect } from 'react';
import { playThemeSound } from '../../../utils/themeAudio';

const FIREWORK_COLORS = [
    ['#fbbf24', '#f59e0b', '#d97706'], // Gold
    ['#38bdf8', '#0284c7', '#0369a1'], // Blue
    ['#a855f7', '#7e22ce', '#6b21a8'], // Purple
    ['#34d399', '#059669', '#047857'], // Emerald
    ['#f43f5e', '#e11d48', '#be123c'], // Crimson
];

const ROCKET_PRESETS = [
    { id: 1, label: 'Gold Star 🎆', color: '#fbbf24', x: 25 },
    { id: 2, label: 'Neon Blue 🚀', color: '#38bdf8', x: 50 },
    { id: 3, label: 'Royal Purple 🎇', color: '#a855f7', x: 75 }
];

const InteractiveFireworks = () => {
    const [fireworks, setFireworks] = useState([]);
    const [flyingRockets, setFlyingRockets] = useState([]);

    const spawnBurst = (xPercent, yPercent, playAudio = true) => {
        if (playAudio) {
            playThemeSound('newyear', 'firework');
        }

        const id = Date.now() + Math.random();
        const colorPalette = FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];
        const particleCount = 24;

        const particles = Array.from({ length: particleCount }).map((_, i) => {
            const angle = (i / particleCount) * 2 * Math.PI;
            const dist = 55 + Math.random() * 75;
            return {
                id: i,
                dx: Math.cos(angle) * dist,
                dy: Math.sin(angle) * dist,
                color: colorPalette[i % colorPalette.length],
                size: 3.5 + Math.random() * 4.5
            };
        });

        setFireworks(prev => [...prev, { id, x: xPercent, y: yPercent, particles }]);

        setTimeout(() => {
            setFireworks(prev => prev.filter(f => f.id !== id));
        }, 1100);
    };

    // Ambient periodic firework bursts
    useEffect(() => {
        const triggerAmbient = () => {
            const x = 15 + Math.random() * 70;
            const y = 15 + Math.random() * 45;
            spawnBurst(x, y, false);

            const nextTime = 3800 + Math.random() * 3500;
            return setTimeout(() => {
                timer = triggerAmbient();
            }, nextTime);
        };

        let timer = triggerAmbient();
        return () => clearTimeout(timer);
    }, []);

    // Global click listener to launch interactive firework on empty page clicks
    useEffect(() => {
        const handleGlobalClick = (e) => {
            // Only trigger if user clicked background or container, not input/button/link
            const tagName = e.target.tagName.toLowerCase();
            if (['input', 'textarea', 'button', 'a', 'select'].includes(tagName)) return;
            if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input')) return;

            const xPercent = (e.clientX / window.innerWidth) * 100;
            const yPercent = (e.clientY / window.innerHeight) * 100;
            spawnBurst(xPercent, yPercent, true);
        };

        window.addEventListener('click', handleGlobalClick, { passive: true });
        return () => window.removeEventListener('click', handleGlobalClick);
    }, []);

    const launchRocket = (rocket) => {
        const launchId = Date.now() + Math.random();
        setFlyingRockets(prev => [...prev, { id: launchId, x: rocket.x, color: rocket.color }]);

        setTimeout(() => {
            setFlyingRockets(prev => prev.filter(r => r.id !== launchId));
            spawnBurst(rocket.x, 25 + Math.random() * 20, true);
        }, 700);
    };

    return (
        <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden select-none">
            {/* Flying Rocket Trails */}
            {flyingRockets.map((r) => (
                <div
                    key={r.id}
                    className="absolute bottom-0 text-xl md:text-2xl transition-all duration-700 ease-out"
                    style={{
                        left: `${r.x}%`,
                        transform: 'translateY(-65vh)',
                        filter: `drop-shadow(0 0 10px ${r.color})`
                    }}
                >
                    🚀 ✨
                </div>
            ))}

            {/* Clickable Rocket Launchers on Bottom-Right */}
            <div className="fixed bottom-5 right-4 md:right-8 z-[68] flex items-center gap-2 pointer-events-auto">
                {ROCKET_PRESETS.map((rocket) => (
                    <button
                        key={rocket.id}
                        type="button"
                        onClick={() => launchRocket(rocket)}
                        className="px-2.5 py-1.5 rounded-xl bg-gray-950/85 hover:bg-gray-900 border border-yellow-500/40 text-yellow-300 text-xs font-bold shadow-lg shadow-black/50 transition-all hover:scale-110 active:scale-95 flex items-center gap-1 group"
                        title={`Click to launch ${rocket.label}!`}
                    >
                        <span className="group-hover:animate-bounce">🚀</span>
                        <span className="hidden sm:inline text-[11px]">{rocket.label}</span>
                    </button>
                ))}
            </div>

            {/* Firework Bursts */}
            {fireworks.map((fw) => (
                <div
                    key={fw.id}
                    className="absolute"
                    style={{ left: `${fw.x}%`, top: `${fw.y}%` }}
                >
                    <div className="absolute -left-3 -top-3 w-6 h-6 rounded-full bg-white blur-sm animate-ping" />
                    {fw.particles.map((p) => (
                        <div
                            key={p.id}
                            className="absolute rounded-full animate-firework"
                            style={{
                                width: `${p.size}px`,
                                height: `${p.size}px`,
                                backgroundColor: p.color,
                                boxShadow: `0 0 10px ${p.color}`,
                                transform: `translate(${p.dx}px, ${p.dy}px)`,
                                transition: 'all 0.9s cubic-bezier(0.1, 0.8, 0.3, 1)',
                            }}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

export default InteractiveFireworks;
